import { graphql } from 'react-apollo'

import { Option } from 'monapt'
import { compose, withPropsOnChange } from 'recompose'

import {
  BankAccount,
  BankCard,
  BankCardOrder,
  BankCardOrderStatus,
  BankCardOrderVerificationStatus,
  BankCardStatus,
  CardDeliveryStatus,
  Eligibility,
  FundingSourceInfo,
  PackageStatus,
  SpendAccount,
} from 'generated/types'

import spendBankCardOrderQuery from './bankCardOrder.query.gql'
import query from './query.gql'

export type ToggleProp = {
  [key: string]: boolean
}

export type Props = {
  cardActivated: boolean
  cardShipped: boolean
  cardDelivered: boolean
  cardStatusLoading: boolean
  hasBankAccount: boolean
  spendEligible: boolean
  bankCardOrder: BankCardOrder
  toggles: ToggleProp
  spendAccount: SpendAccount
  hasPendngActivationCard: boolean
  spendCardOrderStatusLoading: boolean
  cardOnItsWay: boolean
  prefundTransferRequired: boolean
  fundingSourceInfo: FundingSourceInfo
}

type Handler<T = {}> = (props: Props & T) => void

type GqlProps = {
  allBankAccounts: BankAccount[]
  allBankCards: BankCard[]
  allCardTrackingStatuses: CardDeliveryStatus[]
  loading: boolean
  eligibility: Eligibility
  bankCardOrder: BankCardOrder
  spendAccount: SpendAccount
  fundingSourceInfo: FundingSourceInfo
}

export const SUCCESS = BankCardOrderVerificationStatus.Success
export const ACTION_REQUIRED = BankCardOrderVerificationStatus.ActionRequired
export const PENDING = BankCardOrderVerificationStatus.Pending
export const FAILURE = BankCardOrderVerificationStatus.Failure

export const checkPrefundTransferRequired = (bankCardOrder?: BankCardOrder) => {
  const {
    cardOrderStatus,
    addressVerification,
    identityVerification,
    fundingSourceVerification,
    prefundVerification,
  } = bankCardOrder || {}

  return (
    cardOrderStatus === BankCardOrderStatus.ActionRequired &&
    addressVerification?.status === SUCCESS &&
    identityVerification?.status === SUCCESS &&
    fundingSourceVerification?.status === SUCCESS &&
    prefundVerification?.status === ACTION_REQUIRED
  )
}

const withSpendCardStatus = <T = {}>(handler: Handler<T> = (_) => null) =>
  compose(
    graphql<Props, GqlProps, {}, {}>(query, {
      options: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      },
      props: ({
        data: {
          fundingSourceInfo,
          allBankAccounts,
          allBankCards,
          allCardTrackingStatuses,
          loading,
          eligibility,
          spendAccount,
        } = {},
      }) => {
        if (loading) {
          return {
            cardStatusLoading: loading,
          }
        }
        const spendEligible = eligibility?.eligible
        const cardActivated = Option(allBankCards)
          .getOrElse(() => [])
          .some(
            (card: BankCard) => card && card.status === BankCardStatus.Verified,
          )
        const cardDelivered = Option(allCardTrackingStatuses || [])
          .getOrElse(() => [])
          .some(
            (card: CardDeliveryStatus) =>
              card && card.status === PackageStatus.Delivered,
          )
        const hasPendngActivationCard = Option(allBankCards)
          .getOrElse(() => [])
          .some(
            (card: BankCard) =>
              card && card.status === BankCardStatus.PendingVerification,
          )

        return {
          fundingSourceInfo,
          cardActivated,
          cardDelivered,
          cardShipped:
            Option(allCardTrackingStatuses || []).getOrElse(() => []).length >
            0,
          hasBankAccount:
            Option(allBankAccounts).getOrElse(() => []).length > 0,
          cardStatusLoading: loading,
          spendEligible,
          spendAccount,
          hasPendngActivationCard,
        }
      },
    }),
    graphql<Props, GqlProps, {}, {}>(spendBankCardOrderQuery, {
      options: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      },
      props: ({
        data: { bankCardOrder, spendAccount, loading, refetch } = {},
        ownProps,
      }) => {
        const { spendEligible, cardActivated, cardDelivered } = ownProps
        const cardOnItsWay =
          // Users from phase 1 may not have cardOrderStatus
          spendEligible &&
          !cardActivated &&
          !cardDelivered &&
          bankCardOrder?.cardOrderStatus === BankCardOrderStatus.Success &&
          !spendAccount?.isReopenable
        return {
          bankCardOrder,
          cardOnItsWay,
          prefundTransferRequired: checkPrefundTransferRequired(bankCardOrder),
          spendCardOrderStatusLoading: loading,
          refetchSpendBankCardOrder: refetch,
        }
      },
    }),
    withPropsOnChange(
      [
        'cardActivated',
        'cardShipped',
        'cardOnItsWay',
        'spendEligible',
        'bankCardOrder',
        'spendAccount',
        'hasPendngActivationCard',
      ],
      handler,
    ),
  )

export default withSpendCardStatus
